import {
  TEAM_NAME_MAX_LENGTH,
  TEAM_NAME_MIN_LENGTH,
  toUrlFriendly,
  toUrlFriendlyInput,
} from "@salestrak/utilities";

/** A handle is a slug: lowercase letters, digits and hyphens, and a space in
 *  the name it comes from becomes a hyphen. Both bounds are enforced by the
 *  create form and the settings field; the server normalizes again on write. */
export const TEAM_HANDLE_MIN_LENGTH = 2;
export const TEAM_HANDLE_MAX_LENGTH = 32;

/**
 * Typing-friendly normalization: the workspace slug's typing form (accents to
 * plain letters, runs of separators collapsed to one hyphen) with a trailing
 * hyphen kept so the user can type "field-crew" without the separator
 * vanishing under them. `toTeamHandle` is the form the value is validated and
 * submitted in, and the server stores the same slug.
 *
 * A value of separators alone (the single key a field checks before letting
 * a keystroke through) reads as one hyphen, so typing a space or "-" after a
 * word is never blocked.
 */
export function normalizeTeamHandleInput(value: string): string {
  const slug = toUrlFriendlyInput(value);
  if (!slug && value && /^[^\p{L}\p{N}]+$/u.test(value)) return "-";
  return slug.slice(0, TEAM_HANDLE_MAX_LENGTH);
}

/** The handle a team name derives to — "Field Crew" becomes "field-crew". */
export function toTeamHandle(value: string): string {
  return toUrlFriendly(value)
    .slice(0, TEAM_HANDLE_MAX_LENGTH)
    .replace(/-+$/, "");
}

/** True when `handle` is a submittable handle (an empty handle is the form's
 *  call — the server derives one from the name). */
export function isTeamHandle(handle: string): boolean {
  return (
    handle.length >= TEAM_HANDLE_MIN_LENGTH &&
    handle.length <= TEAM_HANDLE_MAX_LENGTH &&
    handle === toTeamHandle(handle)
  );
}

// The team name bounds come from @salestrak/utilities, the pair the server
// holds create and rename to (it also refuses a name another team holds in
// any letter case, which only the server can know).
export { TEAM_NAME_MAX_LENGTH, TEAM_NAME_MIN_LENGTH };

/** Why `name` cannot be saved as a team name, or null when it can. */
export function teamNameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length < TEAM_NAME_MIN_LENGTH) {
    return `Name must be at least ${TEAM_NAME_MIN_LENGTH} characters`;
  }
  if (trimmed.length > TEAM_NAME_MAX_LENGTH) {
    return `Name must be at most ${TEAM_NAME_MAX_LENGTH} characters`;
  }
  return null;
}

/** The handle as it is addressed — "@field-crew". Empty stays empty so a
 *  team with no handle never renders a bare "@". */
export function formatTeamHandle(handle: string | null | undefined): string {
  return handle ? `@${handle}` : "";
}
